import { useState } from 'react'
import { FileText, AlertCircle, Sparkles, Hash, TrendingUp, IndianRupee, Percent, MessageSquare, Send, CheckCircle2, CalendarDays } from 'lucide-react'
import { reportAPI } from '../../services/api'
import { useToast, getErrorMessage } from '../../components/Toast'

export default function EODReportPage() {
  const { success, error: toastError } = useToast()
  const [formData, setFormData] = useState({
    total_registrations: '',
    total_trainings_completed: '',
    gross_fees_received: '',
    net_fees_received: '',
    remarks: '',
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    setError(null)

    try {
      await reportAPI.submit({
        date: new Date().toISOString().slice(0, 10),
        total_registrations: Number(formData.total_registrations),
        total_trainings_completed: Number(formData.total_trainings_completed),
        gross_fees_received: formData.gross_fees_received,
        net_fees_received: formData.net_fees_received || '0',
        remarks: formData.remarks,
      })
      success('End-of-day report submitted successfully.')
      setSubmitted(true)
      setFormData({
        total_registrations: '',
        total_trainings_completed: '',
        gross_fees_received: '',
        net_fees_received: '',
        remarks: '',
      })
      setTimeout(() => setSubmitted(false), 3000)
    } catch (err: any) {
      const msg = getErrorMessage(err, 'Failed to submit report.')
      setError(msg)
      toastError(msg)
    } finally {
      setIsSubmitting(false)
    }
  }

  const inputClass = "w-full px-3.5 py-2.5 bg-neutral-50 border border-neutral-200 rounded-xl text-sm text-neutral-900 placeholder:text-neutral-400 focus:bg-white focus:ring-4 focus:ring-[#E4670F]/10 focus:border-[#E4670F] outline-none transition-all"

  const today = new Date().toLocaleDateString('en-IN', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  })

  return (
    <div className="p-6 md:p-8 bg-[#FAF6EE] min-h-full">
      {/* ── Header ── */}
      <div className="mb-7 flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="inline-flex items-center gap-1.5 bg-[#E4670F]/10 text-[#C24A0A] border border-[#E4670F]/20 rounded-full px-2.5 py-0.5 text-[11px] font-semibold tracking-wide uppercase mb-2">
            <Sparkles className="w-3 h-3" />
            Daily Report
          </div>
          <h1
            className="text-3xl font-bold text-neutral-900 mb-1"
            style={{ fontFamily: 'Georgia, serif' }}
          >
            End-of-Day Report
          </h1>
          <p className="text-sm text-neutral-500">Submit your daily activity report</p>
        </div>
        <div className="flex items-center gap-2 bg-white border border-neutral-200 rounded-xl px-3.5 py-2 shadow-sm">
          <CalendarDays className="w-4 h-4 text-[#C24A0A]" />
          <span className="text-xs font-medium text-neutral-600">{today}</span>
        </div>
      </div>

      {/* ── Required banner ── */}
      <div className="mb-6 flex items-start gap-2.5 bg-blue-50 border border-blue-200 rounded-xl px-4 py-3">
        <AlertCircle className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-semibold text-blue-900">Required Before Check-out</p>
          <p className="text-xs text-blue-800 mt-0.5">
            You must submit this report before you can check out for the day.
          </p>
        </div>
      </div>

      {/* ── Error ── */}
      {error && (
        <div className="mb-6 flex items-start gap-2.5 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm max-w-2xl">
          <span className="w-1.5 h-1.5 rounded-full bg-red-500 mt-1.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* ── Form ── */}
      <div className="max-w-2xl">
        <div className="bg-white rounded-2xl shadow-sm ring-1 ring-neutral-200/70 overflow-hidden">
          {/* Card header */}
          <div className="px-6 sm:px-7 py-4 border-b border-neutral-100 flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#F0821F] to-[#A31621] flex items-center justify-center shadow-md shadow-[#E4670F]/30">
              <FileText className="w-4 h-4 text-white" />
            </div>
            <div>
              <h2 className="text-base font-bold text-neutral-900" style={{ fontFamily: 'Georgia, serif' }}>
                Today's Activity
              </h2>
              <p className="text-xs text-neutral-500">Fill in all required fields to submit</p>
            </div>
          </div>

          {/* Form body */}
          <form onSubmit={handleSubmit} className="p-6 sm:p-7 space-y-5">
            {/* Registrations */}
            <div>
              <label className="block text-xs font-semibold text-neutral-600 mb-1.5">
                Total Registrations <span className="text-red-500">*</span>
              </label>
              <div className="relative group">
                <Hash className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400 group-focus-within:text-[#E4670F] transition-colors pointer-events-none" />
                <input
                  type="number"
                  name="total_registrations"
                  value={formData.total_registrations}
                  onChange={handleChange}
                  min="0"
                  placeholder="0"
                  className={`${inputClass} pl-10 tabular-nums`}
                  required
                />
              </div>
              <p className="text-xs text-neutral-500 mt-1.5">Number of new registrations today</p>
            </div>

            {/* Trainings */}
            <div>
              <label className="block text-xs font-semibold text-neutral-600 mb-1.5">
                Total Trainings Completed <span className="text-red-500">*</span>
              </label>
              <div className="relative group">
                <TrendingUp className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400 group-focus-within:text-[#E4670F] transition-colors pointer-events-none" />
                <input
                  type="number"
                  name="total_trainings_completed"
                  value={formData.total_trainings_completed}
                  onChange={handleChange}
                  min="0"
                  placeholder="0"
                  className={`${inputClass} pl-10 tabular-nums`}
                  required
                />
              </div>
              <p className="text-xs text-neutral-500 mt-1.5">Number of training sessions completed</p>
            </div>

            {/* Gross Fees */}
            <div>
              <label className="block text-xs font-semibold text-neutral-600 mb-1.5">
                Gross Fees Received (₹) <span className="text-red-500">*</span>
              </label>
              <div className="relative group">
                <IndianRupee className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400 group-focus-within:text-[#E4670F] transition-colors pointer-events-none" />
                <input
                  type="number"
                  name="gross_fees_received"
                  value={formData.gross_fees_received}
                  onChange={handleChange}
                  min="0"
                  step="0.01"
                  placeholder="0"
                  className={`${inputClass} pl-10 tabular-nums`}
                  required
                />
              </div>
              <p className="text-xs text-neutral-500 mt-1.5">Total fees collected before deductions</p>
            </div>

            {/* Net Fees */}
            <div>
              <label className="block text-xs font-semibold text-neutral-600 mb-1.5">
                Net Fees Received (₹)
                <span className="text-neutral-400 normal-case font-normal ml-1.5">optional</span>
              </label>
              <div className="relative group">
                <Percent className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400 group-focus-within:text-[#E4670F] transition-colors pointer-events-none" />
                <input
                  type="number"
                  name="net_fees_received"
                  value={formData.net_fees_received}
                  onChange={handleChange}
                  min="0"
                  step="0.01"
                  placeholder="0"
                  className={`${inputClass} pl-10 tabular-nums`}
                />
              </div>
              <p className="text-xs text-neutral-500 mt-1.5">
                Amount after deductions, if applicable (must be ≤ gross fees)
              </p>
            </div>

            {/* Remarks */}
            <div>
              <label className="block text-xs font-semibold text-neutral-600 mb-1.5">
                Remarks
                <span className="text-neutral-400 normal-case font-normal ml-1.5">optional</span>
              </label>
              <div className="relative group">
                <MessageSquare className="absolute left-3.5 top-3.5 w-4 h-4 text-neutral-400 group-focus-within:text-[#E4670F] transition-colors pointer-events-none" />
                <textarea
                  name="remarks"
                  value={formData.remarks}
                  onChange={handleChange}
                  placeholder="Add any additional notes about your day..."
                  rows={4}
                  className={`${inputClass} pl-10 resize-none`}
                />
              </div>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="group w-full flex items-center justify-center gap-2.5 px-6 py-3.5 bg-gradient-to-r from-[#A31621] to-[#7A1F1F] hover:from-[#8A1219] hover:to-[#5F1818] disabled:from-neutral-400 disabled:to-neutral-400 text-white font-semibold rounded-xl transition-all duration-300 shadow-md shadow-[#A31621]/20 hover:shadow-lg hover:shadow-[#A31621]/30 hover:-translate-y-0.5 disabled:hover:translate-y-0 disabled:shadow-none"
            >
              <Send className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
              {isSubmitting ? 'Submitting...' : 'Submit Report'}
            </button>
          </form>
        </div>

        {/* Success */}
        {submitted && (
          <div className="mt-4 flex items-center justify-center gap-2 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3 text-emerald-800 text-sm font-medium">
            <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
            Report submitted successfully!
          </div>
        )}
      </div>
    </div>
  )
}